import { LockIcon, StarIcon } from "@src/assets";
import { Button, ButtonVariants, IconWithTooltip } from "@src/components";
import { SWAPPER_FINANCE_URL } from "@src/config";
import { RoutePath } from "@src/interfaces";
import { TaskStatus, TaskTypes, EnrichedTask } from "@src/interfaces/Rewards";
import { motion } from "framer-motion";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export const TaskRow = ({
  task,
  referralCode,
  onClaim,
  verifyTask,
}: {
  task: EnrichedTask;
  referralCode: string;
  onClaim: (taskId: string) => void;
  verifyTask: (taskId: string) => void;
}) => {
  const [copied, setCopied] = useState(false);

  const {
    name,
    description,
    points,
    status,
    type,
    taskId,
    justClaimed,
    claimed,
  } = task;

  const navigate = useNavigate();

  const completeTask = async () => {
    const copyAndNotify = async (content: string) => {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };

    if (task.type === TaskTypes.Referral) {
      const message = `Want to buy some crypto? Swapper makes it simple 💫 Join with my link: ${SWAPPER_FINANCE_URL}?ref=${referralCode}`;
      await copyAndNotify(message);
      return;
    }

    if (task.type === TaskTypes.CopyContent) {
      const content = task.extraData;
      if (!content) return;

      await copyAndNotify(content);
      return;
    }

    if (task.type === TaskTypes.External) {
      if (task.extraData) {
        window.open(task.extraData, "_blank", "noopener,noreferrer");
      } else {
        console.warn("Missing URL for external task");
      }
      return;
    }

    if (type === TaskTypes.TwitterIntent) {
      const twitterPost = `
Just tried @SwapperFinance and earned my first Swapper Points.

Impressed! You can buy any crypto with your card, instantly.

Want in? Start earning points here: https://swapper.finance/?ref=${referralCode}
      `;
      window.open(
        `https://x.com/intent/tweet?text=${encodeURIComponent(twitterPost)}`,
        "_blank",
      );
      verifyTask(taskId);
      return;
    }

    if (task.type === TaskTypes.Transaction) {
      navigate(RoutePath.Buy);
      return;
    }

    console.error("Unhandled task type:", task.type);
  };

  const backgroundColor =
    status === TaskStatus.Locked
      ? "transparent"
      : justClaimed || claimed
      ? "color-mix(in srgb, var(--swapper-success-color) 10%, transparent)"
      : "var(--swapper-card-bg)";

  const opacityClass =
    status === TaskStatus.Locked ? "opacity-50" : "opacity-100";

  return (
    <motion.div
      className={`flex justify-between p-4 gap-4 items-center rounded-xl ${opacityClass}`}
      initial={false}
      animate={{ backgroundColor }}
      transition={{ duration: 0.3 }}
    >
      <div className={`flex gap-2 items-center`}>
        {status === TaskStatus.Locked && (
          <div className="min-w-4 min-h-4">
            <IconWithTooltip
              id={`rewards-locked-tooltip-${taskId}`}
              tooltipText="You need to complete the previous task to unlock this one."
              icon={LockIcon}
            />
          </div>
        )}
        <div className="flex flex-col">
          <p className="text-sm font-bold text-text_primary">{name}</p>
          <p className="text-xs font-medium text-text_primary/60">
            {description}
          </p>
        </div>
      </div>
      <div className="flex gap-3 items-center">
        <div className="flex items-center">
          <p className="text-text_primary text-xs">+{points}</p>
          <StarIcon className="w-4 h-4" />
        </div>
        <div className="w-[80px]">
          <Button
            variant={
              status === TaskStatus.Unlocked || status === TaskStatus.Locked
                ? ButtonVariants.Outlined
                : ButtonVariants.Primary
            }
            disabled={
              status === TaskStatus.Locked || status === TaskStatus.Claimed
            }
            onClick={async () => {
              if (status === TaskStatus.Completed) {
                onClaim(taskId);
              } else if (status === TaskStatus.Unlocked) {
                await verifyTask(taskId);
                completeTask();
              }
            }}
            className="px-4 py-1"
          >
            {status === TaskStatus.Claimed && "Done"}
            {status === TaskStatus.Completed && "Claim"}
            {(status === TaskStatus.Unlocked ||
              status === TaskStatus.Locked) && (
              <p>
                {type === TaskTypes.Transaction && "Trade"}
                {type === TaskTypes.External && "Open"}
                {type === TaskTypes.TwitterIntent && "Tweet"}
                {(type === TaskTypes.CopyContent ||
                  type === TaskTypes.Referral) &&
                  (copied ? (
                    <div className="flex items-center gap-1">Copied</div>
                  ) : (
                    <div className="flex items-center gap-1">Copy</div>
                  ))}
              </p>
            )}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};
